import { CommonModule } from '@angular/common'
import type { OnInit } from '@angular/core'
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core'
import { RouterModule } from '@angular/router'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Events } from '@ngrx/signals/events'
import { delay, filter, map } from 'rxjs'

import { createProvider } from '@app/core/create-provider'
import { Required } from '@app/core/decorators/required'
import type { ProfileFull, SearchProfile } from '@app/models/profile'
import { SecretAccessButtonComponent } from '@app/modules/access/components/secret-access-key-button/secret-access-button.component'
import { FrameComponent } from '@app/modules/frame'
import { ActivityStatusComponent } from '@app/modules/profile/components/activity-status/activity-status.component'
import { ProfilePhotoComponent } from '@app/modules/profile/components/profile-photo/profile-photo.component'
import { TopAdmirerBadgeComponent } from '@app/modules/top-admirers/components/top-admirer-badge/top-admirer-badge.component'
import { UIModule } from '@app/modules/ui/ui.module'
import { ModalWithHeaderActions } from '@app/overlays/modal/modal-with-header-actions'
import { NotificationsStore } from '@app/overlays/notification/notifications.store'
import { PopoverService } from '@app/overlays/popover/popover.service'
import { ToastService } from '@app/overlays/toast/toast.service'
import { MemberService } from '@app/services/member.service'
import { SiteStore } from '@app/store/site.store'

import type { ConversationProfile } from '../../models/conversation-profile.model'
import { ConversationContentComponent } from '../conversation-content/conversation-content.component'
import { conversationEvents } from '../store/conversation.events'
import { CONVERSATION_STORE_CONFIG, ConversationStore } from '../store/conversation.store'
import { sendingEvents } from '../store/sending.events'

@UntilDestroy()
@Component({
  selector: 'sb-message-modal',
  templateUrl: './message-modal.component.html',
  styleUrls: ['./message-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    UIModule,
    ConversationContentComponent,
    ProfilePhotoComponent,
    RouterModule,
    TopAdmirerBadgeComponent,
    ActivityStatusComponent,
    SecretAccessButtonComponent,
  ],
  providers: [createProvider(CONVERSATION_STORE_CONFIG)({ useValue: { source: 'message-modal' } }), ConversationStore],
})
export class MessageModalComponent extends ModalWithHeaderActions implements OnInit {
  readonly conversationStore = inject(ConversationStore)
  readonly notificationsStore = inject(NotificationsStore)
  readonly popoverService = inject(PopoverService)
  readonly frameComponent = inject(FrameComponent)
  readonly toastService = inject(ToastService)
  readonly events = inject(Events)
  readonly siteStore = inject(SiteStore)
  readonly memberService = inject(MemberService)

  readonly animationShowFinished = this.frameComponent.animationShowFinished
  readonly showSpinner$ = this.frameComponent.animationShowFinished$.pipe(delay(500))

  @Required @Input() profile!: SearchProfile | ProfileFull | ConversationProfile

  ngOnInit(): void {
    this.conversationStore.selectConversation(this.profile.browse_id)

    this.events
      .on(conversationEvents.loadFailure)
      .pipe(
        map(({ payload }) => payload),
        filter(({ profileBrowseId }) => profileBrowseId === this.profile.browse_id),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.close()
      })

    this.events
      .on(sendingEvents.sendMessageSuccess)
      .pipe(
        map(({ payload }) => payload),
        filter(({ conversation }) => conversation.profile.browse_id === this.profile.browse_id),
        filter(({ conversation }) => !conversation.allow_chat),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.popoverService.show('nc-check-circle', 'Message Sent')
        this.close()
      })
  }
}
